import type { Suggestion } from "./types";

// ── Event dimensions — the descriptive attributes we stamp on every telemetry
// event (impression / dismiss / betslip_add / bet_placed) so the backend can
// break engagement down by sport, odds band, bet type, live, stream, and rank.
//
// Everything here is derived from the suggestion the user actually saw — no
// fabrication. Values are stable slugs (not display strings) so aggregation keys
// don't drift. `rank` is the card's 0-based position in the list at the moment,
// which reveals whether users ever engage past the top card.

export interface EventDims {
  sport: string; // normalized slug: "football" | "tennis" | … | "multi" | "unknown"
  is_live: boolean;
  bet_type: "single" | "acca";
  odds_band: string; // "strong_fav" | "fav" | "value" | "outsider" | "none"
  has_stream: boolean;
  rank: number; // 0-based position in the suggestions list
  legs: number; // acca leg count (0 for singles)
  pool_band: string; // which candidate band served this pick: "core" | "regional" | "filler" | "none"
  // Which personal signal, if any, put this card here. `starred` is the ★ route
  // that matched ("event" | "team" | "league"; absent when none); `taste` is how
  // much bet history the profile behind it was built from.
  starred?: string;
  taste?: string;
  /** Which side of the market the card offered: "favorite", or "underdog" when the
   *  visitor's own betting history says they take longer prices. */
  pick_style?: string;
  // The backend's low/medium/high risk tier for this card (match-page tiered
  // lineup). Absent when the backend didn't stamp one.
  tier?: string;
  advice_lane?: "current_match" | "user_priority" | "kickoff_soon" | "parallel_live" | "crowd_momentum";
  // The dropping-odds strategy the server stamped (backed / against / no_drop).
  odds_drop?: string;
  // The other server-graded recommendation strategies.
  crowd_vote?: string;
  line_basis?: string;
  momentum_read?: string;
  trigger_kind?: "current_state" | "price_change" | "line_appeared" | "accepted_combo" | "user_history" | "demand_acceleration" | "parallel_event_change" | string;
  trigger_id?: string;
  /** P4 (2026-10-08): the «N players bet on this match» count the card showed;
   *  absent when it showed none. Read by OPEN 4 item 8 (with vs without). */
  bettors_24h?: number;
  exposure_id?: string;
  // Set only when this leg was described from the board rather than a suggestion
  // we made (a leg the user picked themselves) — see betting-bus.ts.
  league?: string;
  country?: string;
  advice_model?: string;
  advice_ver?: string;
  advice_src?: string;
}

/** Odds → band slug, using the SAME thresholds as the card's confidenceLabel so
 * the band we tune on is the band the user was shown. `null`/0 → "none" (a
 * suspended "one to watch" line with no price). */
export function oddsBand(odds: number | null | undefined): string {
  if (!(typeof odds === "number") || !(odds > 0)) return "none";
  if (odds <= 1.5) return "strong_fav";
  if (odds <= 2.0) return "fav";
  if (odds <= 3.0) return "value";
  return "outsider";
}

/** Collapse a feed sport name to a stable slug (soccer≡football). */
export function normalizeSport(sport: string | undefined): string {
  const s = (sport || "").trim().toLowerCase();
  if (!s) return "unknown";
  // E-sports first: "E-Football"/"E-Basketball" must NOT collapse into the real sport.
  if (/^e[- ]|e-?sport|counter|dota|virtual/.test(s)) return "esports";
  if (/foot|soccer/.test(s)) return "football";
  if (/table\s*tennis|ping/.test(s)) return "table_tennis";
  if (/tennis/.test(s)) return "tennis";
  if (/basket/.test(s)) return "basketball";
  if (/ice\s*hockey|hockey/.test(s)) return "hockey";
  if (/volley/.test(s)) return "volleyball";
  if (/handball/.test(s)) return "handball";
  if (/baseball/.test(s)) return "baseball";
  if (/cricket/.test(s)) return "cricket";
  return s.replace(/\s+/g, "_");
}

/** Compute the descriptive dims for a suggestion at a given list position. */
export function suggestionDims(s: Suggestion, rank: number): EventDims {
  const legs = s.legs ?? [];
  const isAcca = legs.length > 0;

  // Key on the English name (sport_en) — the localized display name fragments
  // aggregation keys per language on FR/AR/RU boards.
  let sport: string;
  if (isAcca) {
    const legSports = [...new Set(legs.map((l) => normalizeSport(l.sport_en ?? l.sport)).filter((x) => x !== "unknown"))];
    sport = legSports.length === 1 ? legSports[0] : "multi";
  } else {
    const se = s as { sport?: string; sport_en?: string };
    sport = normalizeSport(se.sport_en ?? se.sport);
  }

  return {
    sport,
    // The backend marks an accumulator live only when ALL its legs are live.
    // Keep its explicit false for prematch/mixed cards. Older payloads without
    // card state use leg state; unmarked legacy legs retain their old fallback.
    is_live: s.is_live ?? (isAcca && legs.every((leg) => leg.is_live !== false)),
    bet_type: isAcca ? "acca" : "single",
    odds_band: oddsBand(isAcca ? s.combined_odds : s.odds),
    has_stream: !!s.has_stream,
    rank,
    legs: legs.length,
    pool_band: (s as { pool_band?: string }).pool_band || "none",
    ...((s as { starred?: string }).starred ? { starred: (s as { starred?: string }).starred } : {}),
    ...((s as { taste?: string }).taste ? { taste: (s as { taste?: string }).taste } : {}),
    ...((s as { pick_style?: string }).pick_style
      ? { pick_style: (s as { pick_style?: string }).pick_style } : {}),
    ...((s.advice_lane) ? { advice_lane: s.advice_lane } : {}),
    ...((s.odds_drop) ? { odds_drop: s.odds_drop } : {}),
    ...((s.crowd_vote) ? { crowd_vote: s.crowd_vote } : {}),
    ...((s.line_basis) ? { line_basis: s.line_basis } : {}),
    ...((s.momentum_read) ? { momentum_read: s.momentum_read } : {}),
    ...((s.trigger_kind) ? { trigger_kind: s.trigger_kind } : {}),
    ...((s.trigger_id) ? { trigger_id: s.trigger_id } : {}),
    ...(typeof s.bettors_24h === "number" && s.bettors_24h > 0 ? { bettors_24h: s.bettors_24h } : {}),
    ...(s.advice_meta?.arm_model ? { advice_model: s.advice_meta.arm_model } : {}),
    ...(s.advice_meta?.arm_ver ? { advice_ver: s.advice_meta.arm_ver } : {}),
    ...(s.advice_meta?.src ? { advice_src: s.advice_meta.src } : {}),
  };
}


/** The dims an `open_match` event carries. ⛔ NOT `suggestionDims`: open_match
 *  has always sent only the card's tier and exposure, and widening it to the
 *  descriptive set would change every existing cell's match_opens at once.
 *  The graded recommendation strategies join them so their cards can count
 *  opens — `open_match` is a first-class outcome, not a footnote. */
export function openMatchDims(s: Suggestion, exposureId?: string | null): Record<string, string> | undefined {
  const d: Record<string, string> = {
    ...(s.tier ? { tier: s.tier } : {}),
    ...(exposureId ? { exposure_id: exposureId } : {}),
    ...(s.odds_drop ? { odds_drop: s.odds_drop } : {}),
    ...(s.crowd_vote ? { crowd_vote: s.crowd_vote } : {}),
    ...(s.line_basis ? { line_basis: s.line_basis } : {}),
    ...(s.momentum_read ? { momentum_read: s.momentum_read } : {}),
    // ⭐ Why this card / what set it off (owner 2026-09-24: "add isn't our only
    // metric"). Without them the lane's opens read 0 while navigated bets on
    // the same cards already carried the lane into its stake.
    ...(s.advice_lane ? { advice_lane: s.advice_lane } : {}),
    ...(s.trigger_kind ? { trigger_kind: s.trigger_kind } : {}),
    ...(s.advice_meta?.arm_model ? { advice_model: s.advice_meta.arm_model } : {}),
    ...(s.advice_meta?.arm_ver ? { advice_ver: s.advice_meta.arm_ver } : {}),
    ...(s.advice_meta?.src ? { advice_src: s.advice_meta.src } : {}),
  };
  return Object.keys(d).length ? d : undefined;
}

/** The dims stamped on an OUTCOME event (impression / betslip_add / dismiss),
 * i.e. the descriptive dims plus the launcher treatment that was on screen when
 * it happened.
 *
 * The launcher treatment rides here because `pill_impression`/`pill_open` are
 * not funnel outcomes — only on the outcome can it be crossed with a conversion. */
export function outcomeDims(
  s: Suggestion,
  rank: number,
  launcherVariant: string,
  exposureId?: string | null,
): EventDims & { launcher_variant: string } {
  return {
    ...suggestionDims(s, rank),
    ...(s.tier ? { tier: s.tier } : {}),
    launcher_variant: launcherVariant,
    ...(exposureId ? { exposure_id: exposureId } : {}),
  };
}

/**
 * Stamp the launcher treatment onto an OUTCOME payload's `dims`.
 *
 * ⛔ Separate from outcomeDims(): a placement is not built from a Suggestion.
 * `betting-bus.ts` assembles it from the coupon receipt, so the send site is the
 * only place both the treatment and the receipt are in scope.
 */
export function withOutcomeDims(
  meta: Record<string, unknown>,
  launcherVariant: string,
): Record<string, unknown> {
  const dims = (meta.dims as Record<string, unknown> | undefined) ?? {};
  return { ...meta, dims: { ...dims, launcher_variant: launcherVariant } };
}
